import React, { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/outline';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import { createTeam, deleteTeam, getTeams, updateTeam } from '../../services/team';

const TeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });

  const refreshTeams = async () => {
    try {
      setLoading(true);
      setTeams(await getTeams());
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTeams();
  }, []);

  const resetForm = () => {
    setEditingTeam(null);
    setForm({ name: '', description: '' });
  };

  const submitTeam = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (editingTeam) {
        await updateTeam(editingTeam.id, form);
        toast.success('Team updated');
      } else {
        await createTeam(form);
        toast.success('Team created');
      }
      resetForm();
      await refreshTeams();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to save team');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deletingTeam) return;
    try {
      await deleteTeam(deletingTeam.id);
      setDeletingTeam(null);
      toast.success('Team deleted');
      await refreshTeams();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.detail || 'Failed to delete team');
    }
  };

  return (
    <div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl">
        <h1 className="text-2xl font-semibold text-gray-900">Teams</h1>
        <p className="mb-6 mt-1 text-sm text-gray-500">
          Organize employee accounts into teams. Employees remain the ticket requesters.
        </p>

        <form onSubmit={submitTeam} className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-medium text-gray-900">{editingTeam ? 'Edit team' : 'Create a team'}</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">
              Team name
              <input
                required
                maxLength={255}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Description
              <input
                maxLength={255}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2"
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-3">
            {editingTeam && (
              <button type="button" onClick={resetForm} className="rounded-md border px-4 py-2 text-sm">
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingTeam ? 'Save team' : 'Create team'}
            </button>
          </div>
        </form>

        <DeleteConfirmationModal
          isOpen={Boolean(deletingTeam)}
          onClose={() => setDeletingTeam(null)}
          onConfirm={confirmDelete}
          title="Delete Team"
          message={`Delete ${deletingTeam?.name}? Employees must be moved out of this team first.`}
        />

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <p className="py-10 text-center text-sm text-gray-500">Loading teams...</p>
          ) : teams.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">No teams have been created.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">Employees</th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {teams.map((team) => (
                  <tr key={team.id}>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{team.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{team.description || '—'}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{team.employee_count}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        type="button"
                        title="Edit team"
                        className="mr-3 text-indigo-600"
                        onClick={() => {
                          setEditingTeam(team);
                          setForm({ name: team.name, description: team.description || '' });
                        }}
                      >
                        <PencilIcon className="inline h-5 w-5" />
                      </button>
                      <button type="button" title="Delete team" className="text-red-600" onClick={() => setDeletingTeam(team)}>
                        <TrashIcon className="inline h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamsPage;
